import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ChartPeriodSelector } from "@/components/charts";
import { PriceChartContent } from "@/components/charts/PriceChartContent";
import { useStore } from "@/store/useStore";
import { useEffect, useMemo } from "react";
import { TimePeriod } from "@/lib/metal-price";
import { getPeriodText } from "@/lib/utils";
import { processChartData } from "@/lib/metal-price/processor";
import { useWindowSize } from "@/hooks/use-window-size";

interface PriceChartProps {
  title?: string;
  className?: string;
  isHomePage?: boolean;
}

export function PriceChart({ 
  title = "Grafico Prezzi Metalli", 
  className,
  isHomePage = false // isHomePage is kept for potential future use, but doesn't affect period selection anymore
}: PriceChartProps) {
  const {
    goldPriceHistory,
    silverPriceHistory,
    isHistoryLoading,
    error,
    selectedTimePeriod,
    setSelectedTimePeriod,
    fetchPriceHistoryData
  } = useStore();

  const { width } = useWindowSize();
  const isSmallScreen = width < 768;
  
  useEffect(() => {
    // This effect triggers for initial data load and when the period changes.
    // The data fetch is tied to the globally selected time period.
    fetchPriceHistoryData(selectedTimePeriod);
  }, [selectedTimePeriod, fetchPriceHistoryData]);

  const handlePeriodChange = (period: TimePeriod) => {
    setSelectedTimePeriod(period);
  };

  const handleRetryLoading = () => {
    fetchPriceHistoryData(selectedTimePeriod);
  };
  
  const validGoldHistory = goldPriceHistory.filter(p => p.price != null && p.timestamp != null);
  const validSilverHistory = silverPriceHistory.filter(p => p.price != null && p.timestamp != null);
  const chartData = processChartData(validGoldHistory, validSilverHistory);

  const responsiveChartData = useMemo(() => {
    // No downsampling on large screens.
    if (!isSmallScreen) {
      return chartData;
    }

    const originalLength = chartData.length;
    let desiredPoints;

    // Determine the desired number of points based on period and screen width
    if (selectedTimePeriod === 'all') {
      // For the 'all' period on small screens, be very aggressive to ensure readability.
      // We aim for a small number of points (e.g., 7+), representing larger time intervals.
      // This matches the behavior of individual Gold and Silver charts.
      const divisor = 100; // More aggressive divisor
      desiredPoints = Math.max(7, Math.floor(width / divisor));
    } else {
      // For shorter periods, we can afford more data points.
      const divisor = 50; // Standard divisor
      desiredPoints = Math.max(7, Math.floor(width / divisor));
    }

    // If we already have a reasonable number of points, no need to downsample.
    if (originalLength <= desiredPoints) {
      return chartData;
    }

    // Calculate interval to get down to the desired number of points.
    const interval = Math.max(1, Math.floor(originalLength / desiredPoints));
    console.log(`[Chart] Downsampling for '${selectedTimePeriod}' on small screen. Original: ${originalLength}, Target: ${desiredPoints}, Interval: ${interval}, Final Points: ${Math.floor(originalLength / interval)}`);
    
    return chartData.filter((_, i) => i % interval === 0);

  }, [chartData, selectedTimePeriod, isSmallScreen, width]);

  const noData = !isHistoryLoading && responsiveChartData.length === 0;

  return (
    <Card className={cn("w-full border-metal-light/20 shadow-lg", className)}>
      <CardHeader className="flex flex-col gap-4 pb-2 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="font-bold text-2xl metal-text tracking-tight">
          {title}
        </CardTitle>
        <ChartPeriodSelector 
          selectedPeriod={selectedTimePeriod}
          isLoading={isHistoryLoading}
          onPeriodChange={handlePeriodChange}
          disabledPeriods={[]}
        />
      </CardHeader>
      <CardContent className="h-96 pt-6">
        <PriceChartContent
          isLoading={isHistoryLoading}
          error={{ hasError: !!error, message: error || undefined }}
          noData={noData}
          chartData={responsiveChartData}
          selectedTimePeriod={selectedTimePeriod}
          onRetry={handleRetryLoading}
          getPeriodText={getPeriodText}
        />
      </CardContent>
    </Card>
  );
}
